// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { actionListRenderer, ActionListRendererParams } from '@Helpers/actionListRenderer';
import {
    TaskInput,
    TaskOutput,
    TaskStatus,
    AdminPermissions,
    OrgPermissions,
    OrgPermissionsEnum,
    UserOutput,
} from 'thehive-sdk';
import client, { queryClient } from '@Client';
import { ResponderDrawer } from '@Organisms/Responder';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { IsFlag } from '@Organisms/IsFlag/IsFlag';
import RouteBuilder from '@Services/RouteBuilder';
import { PinContextInterface, PinType, PinTypes } from '@Molecules/Pin';
import { ICONS } from '@Constants/iconsApp';
import { TaskBulkEditManage } from './TaskBulkEdit/TaskBulkEditManage';
import { computeGetSelector } from '@Organisms/Case/selector';
import { UseLocalMessageType } from '@Helpers/locale';

const computeDataPin =
    (caseId: string) =>
    ({ _id, title }: Partial<TaskOutput>): PinType | null => {
        const url = RouteBuilder.compute('cases.task', { caseId, taskId: _id });
        return _id && title ? { caseId, type: PinTypes.TASK, id: _id, title, url } : null;
    };

const convertDataToPin = (getDataPin: (...args: any[]) => any, selectedItems: TaskOutput[]): PinType[] =>
    selectedItems.reduce((acc: PinType[], curr): PinType[] => {
        const result = getDataPin(curr);
        return result !== null ? [...acc, result] : acc;
    }, [] as PinType[]);

export const actionList = (
    locale: UseLocalMessageType,
    history: any,
    caseId: string,
    pinContext?: Partial<PinContextInterface>,
    permissions: (AdminPermissions | OrgPermissions)[] = [],
    currentUserData?: UserOutput,
): ActionListRendererParams<TaskOutput> =>
    actionListRenderer<TaskOutput>((selectedItems, onSuccess, from) => {
        const isMultiple = selectedItems.length > 1 ? 'multiple' : 'single';

        const { removeMultiPin, addMultiPin, isPinned, getLimit } = pinContext || {};

        const taskIdList = selectedItems.map(({ _id }) => _id);
        const getDataPin = computeDataPin(caseId);
        const limitPin = getLimit?.({ caseId, type: PinTypes.TASK });

        return {
            assigntome: {
                label: locale('common.assigntome'),
                icon: <ICONS.UserPlus />,
                hidden: !(
                    permissions.includes(OrgPermissionsEnum.MANAGE_TASK) &&
                    selectedItems?.some(({ assignee }) => assignee !== currentUserData?.login)
                ),
                scope: 'both',
                query: () => {
                    client.TaskController.multipleEdit<Partial<TaskInput>, void>(
                        { assignee: currentUserData?.login },
                        taskIdList,
                    ).then(() => {
                        onSuccess(false);
                        openMessage('success', locale('notification.task.assigntome'));
                    });
                },
            },
            bulkedit: {
                scope: 'list',
                CustomComponent: (triggerElem) => (
                    <TaskBulkEditManage
                        triggerElem={triggerElem}
                        onEdit={({ assignee, group }) =>
                            client.TaskController.multipleEdit<Partial<TaskInput>, void>(
                                { assignee, group },
                                taskIdList,
                            ).then(() => {
                                onSuccess(false);
                            })
                        }
                        caseId={caseId}
                    />
                ),
                label: locale('general.edit'),
                icon: <ICONS.EditIcon />,
                hidden: !permissions.includes(OrgPermissionsEnum.MANAGE_TASK),
            },
            start: {
                query: () => {
                    client.TaskController.multipleEdit<Partial<TaskInput>, void>(
                        { status: TaskStatus.IN_PROGRESS, endDate: null },
                        taskIdList,
                    )
                        .then(() => {
                            onSuccess();
                            queryClient.refetchQueries([computeGetSelector({ caseId }).name]);
                        })
                        .catch(handleNotificationError);
                },
                message: locale(`task.actions.start.${isMultiple}`),
                label: locale('task.actions.start.label'),
                hidden:
                    !permissions.includes(OrgPermissionsEnum.MANAGE_TASK) ||
                    selectedItems.some(({ status }) => status === TaskStatus.IN_PROGRESS),
                icon: <ICONS.StartIcon />,
            },
            close: {
                query: () => {
                    client.TaskController.multipleEdit<Partial<TaskInput>, void>(
                        { status: TaskStatus.COMPLETED },
                        taskIdList,
                    )
                        .then(() => {
                            onSuccess();
                            queryClient.refetchQueries([computeGetSelector({ caseId }).name]);
                        })
                        .catch(handleNotificationError);
                },
                message: locale(`task.actions.close.${isMultiple}`),
                label: locale('task.actions.close.label'),
                hidden:
                    !permissions.includes(OrgPermissionsEnum.MANAGE_TASK) ||
                    selectedItems.some(({ status, mandatory = false, extraData = {} }) => {
                        const { logCount } = extraData;

                        if (logCount == 0 && mandatory) {
                            return true;
                        }

                        return [TaskStatus.COMPLETED, TaskStatus.WAITING].includes(status);
                    }),
                icon: <ICONS.CloseIcon />,
            },
            delete: {
                query: async () => {
                    try {
                        await client.TaskController.multipleDelete(taskIdList);
                        openMessage('success', locale('notification.task.delete'));
                        if (from === 'details') {
                            history.push(RouteBuilder.compute('cases.tasks', { caseId }));
                        } else {
                            onSuccess(true);
                        }
                    } catch (error) {
                        handleNotificationError(error);
                    }
                },
                message: locale(`task.actions.delete.${isMultiple}`),
                label: locale('task.actions.delete.label'),
                icon: <ICONS.DeleteIcon />,
                confirm: true,
                hidden: !permissions.includes(OrgPermissionsEnum.MANAGE_TASK),
            },
            flag: {
                query: () => {
                    client.TaskController.multipleEdit<Partial<TaskInput>, void>({ flag: true }, taskIdList)
                        .then(() => onSuccess(true))
                        .catch(handleNotificationError);
                },
                label: locale('task.actions.flag.label'),
                hidden: !permissions.includes(OrgPermissionsEnum.MANAGE_TASK) || selectedItems.some(({ flag }) => flag),
                scope: 'both',
                icon: <IsFlag value={true} />,
            },
            unflag: {
                query: () => {
                    client.TaskController.multipleEdit<Partial<TaskInput>, void>({ flag: false }, taskIdList)
                        .then(() => onSuccess(true))
                        .catch(handleNotificationError);
                },
                label: locale('task.actions.unflag.label'),
                hidden:
                    !permissions.includes(OrgPermissionsEnum.MANAGE_TASK) || selectedItems.some(({ flag }) => !flag),
                scope: 'both',
                icon: <IsFlag value={false} />,
            },

            unpin: {
                query: () => {
                    const dataPins = convertDataToPin(getDataPin, selectedItems);
                    removeMultiPin?.(dataPins);
                },
                label: locale('common.unpin'),
                hidden: !removeMultiPin || selectedItems.some((data) => !isPinned?.(getDataPin(data))),
                scope: 'both',
                icon: <ICONS.UnpinIcon />,
            },
            pin: {
                query: () => {
                    const dataPins = convertDataToPin(getDataPin, selectedItems);
                    addMultiPin?.(dataPins);
                },
                label: locale('common.pin'),
                disabled: !limitPin,
                hidden: !addMultiPin || selectedItems.some((data) => isPinned?.(getDataPin(data))),
                scope: 'both',
                icon: <ICONS.PinIcon />,
            },
            responders: {
                label: locale('task.actions.responders.label'),
                icon: <ICONS.RunResponderIcon />,
                hidden: !permissions.includes(OrgPermissionsEnum.MANAGE_ACTION) || taskIdList.length > 1,
                CustomComponent: (triggerElem): ReactElement => (
                    <ResponderDrawer
                        objectId={taskIdList.find(Boolean) || ''}
                        objectType="case_task"
                        triggerElem={triggerElem}
                    />
                ),
            },
        };
    });
